import {
  addCotenant,
  GqlInitUnderwritingAsTenantRequestType,
  GqlInitUnderwritingAsTenantResponseType,
  InitUnderwritingAsTenantSchema,
} from './../schemas';
import { useMutation } from '@apollo/client';
import {
  EstateApplicationCommandActions,
  InitUnderwritingAsTenantCommand,
  InitializeUnderwriting,
  AddCotenantT,
  AddCotenantCommand,
  ApplicationId,
} from './../../../domain';
import { AddCotenantResponseDto, AddCotenantRequestDto } from '../dtos';
import { ResponseApolloErrorMapping } from '@shared/data-mappers';
import { AptunoApolloLink } from '@shared/data-access';
import { TenantInterestedOnAppliedEstateCommand } from '@contexts/Estate/EstateApplication/domain/commands/tenant-interested-on-applied-estate.command';
import { AptunoError, BaseMutationResponse2, Response } from '@shared/models';
import {
  tenantInterestedOnAppliedEstateMutation,
  TenantInterestedOnAppliedEstateRequestDto,
  TenantInterestedOnAppliedEstateResponseDto,
} from '../schemas/mutations/tenant-interested-on-applied-estate.mutation';
import React, { useMemo } from 'react';

export const GqlEstateApplicationCommandActions: EstateApplicationCommandActions =
  {
    useInitializeEstateApplication: (): InitializeUnderwriting => {
      const [action, { loading, data, error }] = useMutation<
        GqlInitUnderwritingAsTenantResponseType,
        GqlInitUnderwritingAsTenantRequestType
      >(InitUnderwritingAsTenantSchema, {
        errorPolicy: 'all',
        context: { clientName: AptunoApolloLink.CREDIT_STUDY },
      });

      const errorResult: AptunoError | undefined = React.useMemo(
        () => error && ResponseApolloErrorMapping(error),
        [error]
      );

      return {
        loading,
        initializedUnderwriting: data?.initializeUnderwriting.value,
        error: errorResult,
        initUndewritingAsTenant: (command: InitUnderwritingAsTenantCommand) =>
          action({
            variables: GqlInitUnderwritingAsTenantRequestType.from(command),
          }),
      };
    },

    useAddCotenant: (): AddCotenantT => {
      const [action, { loading, data, error }] = useMutation<
        AddCotenantResponseDto,
        AddCotenantRequestDto
      >(addCotenant, {
        errorPolicy: 'all',
        context: { clientName: AptunoApolloLink.CREDIT_STUDY },
      });

      const errorResult: AptunoError | undefined = useMemo(
        () => error && ResponseApolloErrorMapping(error),
        [error]
      );

      return {
        loading,
        addedCotenant: data?.addCotenant.value,
        error: errorResult,
        addCotenant: (command: AddCotenantCommand) =>
          action({
            variables: {
              estateApplicationId: command.estateApplicationId.value,
            },
          }),
      };
    },

    useTenantInterestedOnAppliedEstate: (): BaseMutationResponse2<
      TenantInterestedOnAppliedEstateCommand,
      ApplicationId
    > => {
      const [action, { loading, data, error }] = useMutation<
        TenantInterestedOnAppliedEstateResponseDto,
        TenantInterestedOnAppliedEstateRequestDto
      >(tenantInterestedOnAppliedEstateMutation, {
        errorPolicy: 'all',
        context: { clientName: AptunoApolloLink.CREDIT_STUDY },
      });

      const result: Response<ApplicationId> = useMemo(
        () =>
          !!error
            ? new Response.Failure(ResponseApolloErrorMapping(error))
            : !!data?.addTenantInterestOnAppliedEstate?.value
            ? new Response.Success(
                new ApplicationId(data.addTenantInterestOnAppliedEstate.value)
              )
            : Response.notPerformed,
        [error, data]
      );

      return {
        result,
        executing: loading,
        execute: (command: TenantInterestedOnAppliedEstateCommand) =>
          action({
            variables: <TenantInterestedOnAppliedEstateRequestDto>{
              estateId: `${command.estate.value}`,
            },
          }),
      };
    },
  };
